import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Crown, Clock, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";

/**
 * Header subscription CTA button - visible on every page
 * Shows different states based on subscription status
 */
export function HeaderSubscriptionCTA() {
  const navigate = useNavigate();
  const { subscription, isLoading } = useSubscription();

  if (isLoading) return null;
  if (!subscription) return null;

  const { status, daysRemaining } = subscription;

  // Don't show for active subscriptions with more than 30 days remaining
  if (status === 'active' && daysRemaining > 30) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="gap-2 text-muted-foreground hover:text-foreground"
        onClick={() => navigate('/business/subscription')}
      >
        <Crown className="h-4 w-4" />
        <span className="hidden sm:inline">Subscription</span>
      </Button>
    );
  }

  // Trial states
  if (status === 'trial' || status === 'trial_pending_mandate') {
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "gap-2 border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10",
          status === 'trial_pending_mandate' && "animate-pulse"
        )}
        onClick={() => navigate('/business/subscription')}
      >
        <Clock className="h-4 w-4" />
        <span className="hidden sm:inline">
          {status === 'trial_pending_mandate' ? 'Start Trial' : `Trial: ${daysRemaining}d`}
        </span>
        <span className="sm:hidden">
          {status === 'trial_pending_mandate' ? 'Start' : `${daysRemaining}d`}
        </span>
      </Button>
    );
  }

  // Expiring soon
  if (status === 'expiring_soon') {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-2 border-orange-500/50 text-orange-700 dark:text-orange-400 hover:bg-orange-500/10"
        onClick={() => navigate('/business/subscription')}
      >
        <AlertTriangle className="h-4 w-4" />
        <span className="hidden sm:inline">Renew: {daysRemaining}d left</span>
        <span className="sm:hidden">{daysRemaining}d</span>
      </Button>
    );
  }

  // Expired, grace, or suspended
  if (status === 'expired' || status === 'grace' || status === 'suspended') {
    return (
      <Button
        size="sm"
        className="gap-2 bg-destructive hover:bg-destructive/90"
        onClick={() => navigate('/business/subscription')}
      >
        <CreditCard className="h-4 w-4" />
        <span className="hidden sm:inline">
          {status === 'suspended' ? 'Reactivate Now' : 'Renew Now'}
        </span>
        <span className="sm:hidden">Renew</span>
      </Button>
    );
  }

  // Active with less than 30 days
  return (
    <Button
      variant="ghost"
      size="sm"
      className="gap-2"
      onClick={() => navigate('/business/subscription')}
    >
      <Crown className="h-4 w-4 text-primary" />
      <span className="hidden sm:inline">Manage Plan</span>
    </Button>
  );
}
